// Web security (PLAN §11; task 43): the response headers every page and API answer carries, the strict
// Content-Security-Policy of the app, the headers of a file download (originals are never served inline), and the
// small output-sanitizing helpers used wherever stored or AI-written text meets HTML or a link.
//
// One source of truth: the API Worker applies these in middleware, `apps/web/public/_headers` carries the same
// values for the static app (a test keeps the two in step), and the staging smoke test checks them live.

/** Headers on every response, page or API. */
export const BASE_SECURITY_HEADERS: Readonly<Record<string, string>> = {
  // Two years, subdomains included; the app is HTTPS-only (Cloudflare custom domains).
  'Strict-Transport-Security': 'max-age=63072000; includeSubDomains',
  'X-Content-Type-Options': 'nosniff',
  // Investigation URLs (entity ids, searches) never leave with a click on an outside link.
  'Referrer-Policy': 'no-referrer',
  // Legacy twin of `frame-ancestors 'none'` for older browsers.
  'X-Frame-Options': 'DENY',
  'Cross-Origin-Opener-Policy': 'same-origin',
  'Cross-Origin-Resource-Policy': 'same-origin',
  'Permissions-Policy': 'camera=(), microphone=(), geolocation=(), payment=(), usb=(), serial=(), bluetooth=()',
};

/**
 * The app's Content-Security-Policy. Scripts only from the app itself (no inline script, no eval), nothing framed
 * and nothing framing it, no plugins, no `<base>`, forms post only to the app. Styles allow inline `style`
 * attributes and injected `<style>` (dialog scroll-lock, map controls); scripts stay strict, which is what stops
 * stored XSS. `blob:` workers are the map renderer's; `data:`/`blob:` images are graph snapshots and exports.
 */
export const APP_CSP_DIRECTIVES: Readonly<Record<string, string>> = {
  'default-src': "'self'",
  'script-src': "'self'",
  'style-src': "'self' 'unsafe-inline'",
  'img-src': "'self' data: blob:",
  'font-src': "'self' data:",
  'connect-src': "'self'",
  'media-src': "'self' blob:",
  'worker-src': "'self' blob:",
  'child-src': "'self' blob:",
  'frame-src': "'none'",
  'frame-ancestors': "'none'",
  'object-src': "'none'",
  'base-uri': "'none'",
  'form-action': "'self'",
  'manifest-src': "'self'",
};

const cspText = (d: Readonly<Record<string, string>>) =>
  Object.entries(d)
    .map(([k, v]) => `${k} ${v}`)
    .join('; ');

export const APP_CSP = cspText(APP_CSP_DIRECTIVES);

/**
 * API answers are data, never documents: if one is ever opened as a page it runs nothing, loads nothing and
 * can't be framed (`sandbox` gives it an opaque origin, so it can't read the app's cookies or storage either).
 */
export const API_CSP = "default-src 'none'; frame-ancestors 'none'; sandbox";

/** A downloaded file, if a browser ever renders it anyway: sandboxed, nothing loaded, nothing run. */
export const DOWNLOAD_CSP = "default-src 'none'; sandbox";

/** Every header the static app (HTML, JS, CSS) is served with. */
/**
 * Every header a page or static file is served with. `no-transform` stops Cloudflare's edge from rewriting pages, so
 * Web Analytics can't inject its beacon (a third-party script the CSP refuses, one console error on every screen);
 * the rest is the Workers Static Assets default (always revalidate, so a deploy is seen at once).
 */
export const APP_HEADERS: Readonly<Record<string, string>> = {
  ...BASE_SECURITY_HEADERS,
  'Content-Security-Policy': APP_CSP,
  'Cache-Control': 'public, max-age=0, must-revalidate, no-transform',
};

/** Every header an API answer is served with (a route may set a stricter CSP or its own cache policy). */
export const API_HEADERS: Readonly<Record<string, string>> = { ...BASE_SECURITY_HEADERS, 'Content-Security-Policy': API_CSP };

/** The `_headers` file of the static app (Workers Static Assets): every path gets APP_HEADERS. */
export function staticHeadersFile(): string {
  const lines = Object.entries(APP_HEADERS).map(([k, v]) => `  ${k}: ${v}`);
  return ['# Generated from packages/shared/src/security.ts (task 43); a test fails if the two differ.', '/*', ...lines, ''].join('\n');
}

/**
 * `Content-Disposition` of a download: always an attachment, with an ASCII fallback name (quotes, backslashes and
 * control characters removed) and the exact UTF-8 name for browsers that read `filename*`.
 */
export function attachmentDisposition(filename: string): string {
  const name = filename.split(/[\\/]/).pop()?.trim() || 'file';
  const ascii =
    name
      // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters can't go in a header value
      .replace(/[\u0000-\u001f\u007f"\\;]/g, '_')
      .replace(/[^\x20-\x7e]/g, '_')
      .slice(0, 150) || 'file';
  return `attachment; filename="${ascii}"; filename*=UTF-8''${encodeURIComponent(name).replace(/['()*]/g, c => `%${c.charCodeAt(0).toString(16).toUpperCase()}`)}`;
}

/**
 * Headers of an original file download (PLAN §11): never rendered by the browser whatever it contains (HTML, SVG,
 * a script pretending to be a PDF): a generic byte type, no sniffing, an attachment, a sandbox CSP, never cached.
 */
export function downloadHeaders(filename: string, size?: number): Record<string, string> {
  return {
    ...BASE_SECURITY_HEADERS,
    'Content-Type': 'application/octet-stream',
    'Content-Disposition': attachmentDisposition(filename),
    'Content-Security-Policy': DOWNLOAD_CSP,
    'Cache-Control': 'private, no-store',
    ...(size !== undefined ? { 'Content-Length': String(size) } : {}),
  };
}

// ── Output sanitizing ────────────────────────────────────────────────────────────────────────────────────

/** Text placed in HTML (exports, emails, snapshots): every markup character escaped. */
export const escapeHtml = (s: string) => s.replace(/[&<>"']/g, ch => `&#${ch.charCodeAt(0)};`);

const SAFE_SCHEMES = new Set(['http:', 'https:', 'mailto:']);

/**
 * A link target that is safe to put in `href`: an app path (`/library/…`, not `//host`), an in-page anchor, or an
 * http(s)/mailto address. Anything else (`javascript:`, `data:`, `vbscript:`, obfuscated schemes) → undefined, so
 * stored or AI-written text can never become a script link.
 */
export function safeHref(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  // Browsers drop tabs/newlines and leading control characters/spaces inside URLs (`java\nscript:`).
  const href = raw
    .replace(/[\t\n\r]/g, '')
    // biome-ignore lint/suspicious/noControlCharactersInRegex: the characters browsers strip around a URL
    .replace(/^[\u0000-\u0020]+|[\u0000-\u0020]+$/g, '');
  if (!href) return undefined;
  if (href.startsWith('#')) return href;
  if (href.startsWith('/')) return href.startsWith('//') || href.startsWith('/\\') ? undefined : href;
  try {
    const url = new URL(href);
    return SAFE_SCHEMES.has(url.protocol) ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Where to go after signing in (`/login?next=…`): an app path only. `//host`, `/\\host` (which browsers read as
 * `//host`) and paths hiding tabs or newlines are refused, so the sign-in page can't send someone to another site
 * (security review, task 46).
 */
export function safeNextPath(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !raw.startsWith('/')) return undefined;
  const href = safeHref(raw);
  return href?.startsWith('/') && !href.includes('\\') ? href : undefined;
}

/** Is a link target outside the app (opens in a new tab with no opener and no referrer)? */
export const isExternalHref = (href: string) => /^(https?|mailto):/i.test(href);
